import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectActiveParty } from '../../../redux/editorConfigSlice';
import './UserCard.css';
import API_URL from '../../../config';

const PLAN_LABELS = {
  free: 'Ücretsiz',
  starter: 'Başlangıç',
  pro: 'Profesyonel',
  business: 'Kurumsal',
};

const PLAN_COLORS = {
  free: '#888',
  starter: '#60a5fa',
  pro: '#a78bfa',
  business: '#f59e0b',
};

const GuestIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const UserCard = () => {
  const [user, setUser] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const shareAccess = useSelector((state) => state.project.shareAccess);
  const activeParty = useSelector(selectActiveParty);

  useEffect(() => {
    const stored = localStorage.getItem('projabze_user');
    if (stored) setUser(JSON.parse(stored));
  }, []);

  // Storage event listener to pick up changes from Settings page etc.
  useEffect(() => {
    const onStorage = () => {
      const stored = localStorage.getItem('projabze_user');
      if (stored) setUser(JSON.parse(stored));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ─── Guest card for share mode (no logged-in user) ───
  if (!user && shareAccess) {
    const roleName = activeParty?.name || 'Misafir';
    return (
      <div className="user-card" ref={menuRef}>
        <button className="user-card-btn" onClick={() => setMenuOpen(prev => !prev)}>
          <span className="user-card-initials guest"><GuestIcon /></span>
          <div className="user-card-info">
            <span className="user-card-name">Misafir Kullanici</span>
            <span className="user-card-plan" style={{ color: '#e67e22' }}>{roleName}</span>
          </div>
          <svg className="user-card-chevron" viewBox="0 0 10 6" width="10" height="6">
            <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        {menuOpen && (
          <div className="user-card-menu">
            <div className="user-card-menu-header">
              <span className="user-card-menu-name">Misafir Kullanici</span>
              <span className="user-card-menu-email">{roleName} olarak goruntuleniyor</span>
            </div>
            <div className="user-card-menu-sep" />
            <div className="user-card-menu-item" onClick={() => { setMenuOpen(false); navigate('/login'); }}>
              Giris Yap
            </div>
            <div className="user-card-menu-item" onClick={() => { setMenuOpen(false); navigate('/register'); }}>
              Kayit Ol
            </div>
          </div>
        )}
      </div>
    );
  }

  if (!user) return null;

  const initials = `${(user.firstName || '')[0] || ''}${(user.lastName || '')[0] || ''}`.toUpperCase();
  const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Kullanıcı';
  const planCode = user.plan || 'free';
  const planLabel = PLAN_LABELS[planCode] || planCode;
  const planColor = PLAN_COLORS[planCode] || '#888';
  const avatarUrl = user.avatar
    ? `${API_URL}${user.avatar}`
    : null;

  const handleLogout = () => {
    localStorage.removeItem('projabze_token');
    localStorage.removeItem('projabze_user');
    navigate('/');
  };

  return (
    <div className="user-card" ref={menuRef}>
      <button className="user-card-btn" onClick={() => setMenuOpen(prev => !prev)}>
        {avatarUrl ? (
          <img className="user-card-avatar" src={avatarUrl} alt={fullName} />
        ) : (
          <span className="user-card-initials">{initials}</span>
        )}
        <div className="user-card-info">
          <span className="user-card-name">{fullName}</span>
          {activeParty?.name && (
            <span className="user-card-role">{activeParty.name}</span>
          )}
          <span className="user-card-plan" style={{ color: planColor }}>{planLabel}</span>
        </div>
        <svg className="user-card-chevron" viewBox="0 0 10 6" width="10" height="6">
          <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {menuOpen && (
        <div className="user-card-menu">
          <div className="user-card-menu-header">
            <span className="user-card-menu-name">{fullName}</span>
            <span className="user-card-menu-email">{user.email || ''}</span>
          </div>
          <div className="user-card-menu-sep" />
          <div className="user-card-menu-item" onClick={() => { setMenuOpen(false); navigate('/dashboard'); }}>
            Dashboard
          </div>
          <div className="user-card-menu-item" onClick={() => { setMenuOpen(false); navigate('/projects'); }}>
            Projelerim
          </div>
          <div className="user-card-menu-item" onClick={() => { setMenuOpen(false); navigate('/settings'); }}>
            Ayarlar
          </div>
          <div className="user-card-menu-sep" />
          <div className="user-card-menu-item logout" onClick={handleLogout}>
            Çıkış Yap
          </div>
        </div>
      )}
    </div>
  );
};

export default UserCard;
